import {
  Button,
  MenuItem,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import {
  UploadCloud,
  FileText,
  Layers,
  CheckCircle,
  XCircle,
  Plus,
  Download,
  Trash2,
  Edit2,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { styled } from "@mui/material/styles";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// ─── Reusable confirmation dialog ───────────────────────────────────
function ConfirmDialog({
  open,
  title = "Xác nhận",
  message = "Bạn có chắc chắn?",
  cancelText,
  confirmText,
  onCancel,
  onConfirm,
}) {
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle className="text-red-600 font-bold">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} className="normal-case text-gray-600">
          {cancelText || "Hủy"}
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          className="normal-case"
        >
          {confirmText || "Xác nhận"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Section Excel Buttons (Download Sample + Import) ────────────────────────
function SectionExcelButtons({
  section,
  handleDownloadSampleForSection,
  handleImportExcelForSection,
}) {
  return (
    <>
      <Button
        variant="outlined"
        className="h-8 text-xs border-red-500 text-red-600 hover:bg-red-50 normal-case"
        startIcon={<Download size={14} />}
        onClick={() => handleDownloadSampleForSection(section)}
      >
        Download Sample
      </Button>
      <Button
        component="label"
        variant="outlined"
        className="h-8 text-xs border-red-500 text-red-600 hover:bg-red-50 normal-case"
        startIcon={<UploadCloud size={14} />}
      >
        Import Excel
        <VisuallyHiddenInput
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={(e) => handleImportExcelForSection(section, e)}
        />
      </Button>
    </>
  );
}

// ─── Preview helpers ───────────────────────────────────────────────────────────

// build display list merging consecutive MT questions into groups
function buildDisplayItems(questions) {
  const items = [];
  let i = 0;
  while (i < questions.length) {
    const q = questions[i];
    if (q.matchGroup) {
      // gather contiguous same-group entries
      const group = [];
      let j = i;
      while (j < questions.length && questions[j].matchGroup === q.matchGroup) {
        group.push(questions[j]);
        j++;
      }
      const startIndex = group[0]?._origIndex ?? i;
      items.push({ type: "group", questions: group, startIndex });
      i = j;
    } else {
      const index = q?._origIndex ?? i;
      items.push({ type: "single", question: q, index });
      i++;
    }
  }
  return items;
}

function normalizeDsAnswer(answer) {
  if (answer === true) return "A";
  if (answer === false) return "B";

  const value = String(answer || "").trim().toUpperCase();
  if (value === "A" || value === "TRUE") return "A";
  if (value === "B" || value === "FALSE") return "B";

  return "";
}

// Calculate total points from all questions
function calculateTotalPoints(questions) {
  if (!Array.isArray(questions)) return 0;
  return questions.reduce((sum, q) => {
    const point = typeof q.point === "number" ? q.point : 0;
    return sum + point;
  }, 0);
}

function normalizeChoiceAnswer(answer) {
  const value = String(answer || "").trim().toUpperCase();
  return /^[A-Z]$/.test(value) ? value : "";
}

function getQuestionNumber(questionItem) {
  const matched = String(questionItem?.question || "").match(/(\d+)/);
  return matched ? Number(matched[1]) : undefined;
}

function getPointByQuestionNumber(examType, questionNumber) {
  const normalizedType = String(examType || "").toUpperCase();

  if (!Number.isFinite(questionNumber) || questionNumber <= 0) return undefined;

  if (normalizedType === "HSK1") {
    if (questionNumber <= 5) return 4;
    if (questionNumber <= 15) return 5;
    if (questionNumber <= 20) return 6;
    if (questionNumber <= 25) return 4;
    if (questionNumber <= 35) return 5;
    if (questionNumber <= 40) return 6;
    return undefined;
  }

  if (normalizedType === "HSK2") {
    if (questionNumber <= 10) return 2;
    if (questionNumber <= 30) return 3;
    if (questionNumber <= 35) return 4;
    if (questionNumber <= 45) return 3;
    if (questionNumber <= 50) return 4;
    if (questionNumber <= 60) return 5;
    return undefined;
  }

  if (normalizedType === "HSK3") {
    if (questionNumber <= 40) return 2.5;
    if (questionNumber <= 60) return 3;
    if (questionNumber <= 70) return 4;
    if (questionNumber <= 80) return 10;
    return undefined;
  }

  if (normalizedType === "HSK4") {
    if (questionNumber <= 25) return 2;
    if (questionNumber <= 85) return 2.5;
    if (questionNumber <= 95) return 6;
    if (questionNumber <= 100) return 8;
    return undefined;
  }

  if (normalizedType === "D4") {
    return 0.2;
  }

  return undefined;
}

function resolveQuestionPoint(questionItem, examType) {
  const parsed = Number(String(questionItem?.point ?? "").replace(",", "."));
  if (Number.isFinite(parsed)) return parsed;
  const fixedPoint = getPointByQuestionNumber(examType, getQuestionNumber(questionItem));
  if (fixedPoint !== undefined) return fixedPoint;
  return undefined;
}

function formatPoint(point) {
  if (!Number.isFinite(point)) return "";
  if (Number.isInteger(point)) return String(point);
  return String(point);
}

const GroupPreviewItem = ({ group, startIndex, onDelete, onEdit, onMoveUp, onMoveDown, isFirst, isLast, examType }) => {
  const first = group[0];
  const displayNum = startIndex + 1;
  const firstPoint = resolveQuestionPoint(first, examType);
  return (
    <>
      <div className="p-5 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-all">
        <div className="flex gap-4">
          {/* Number in circle */}
          <div className="flex-shrink-0">
            <div className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center font-bold text-sm border border-red-100">
              {displayNum}
            </div>
            {/* Type badge */}
            <div className="mt-2 text-center">
              <span className="text-[10px] font-bold text-gray-400 uppercase border border-gray-200 px-1 rounded bg-gray-50">
                MT
              </span>
            </div>
            {firstPoint !== undefined && (
              <div className="mt-2 text-center text-[11px] font-semibold text-red-600">
                {formatPoint(firstPoint)} điểm
              </div>
            )}
          </div>

          <div className="flex-grow">
            {first.imageUrl && (
              <div className="mb-4">
                <img
                  src={first.imageUrl}
                  alt="Question visual"
                  className="max-h-48 rounded-lg border border-gray-100"
                />
              </div>
            )}
            {first.contentQuestions && (
              <div className="text-gray-800 mb-4 whitespace-pre-wrap leading-relaxed font-medium">
                {first.contentQuestions}
              </div>
            )}
            <div className="space-y-2">
              {group.map((q, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="font-bold text-gray-700 min-w-[1.5rem]">
                    {idx + 1}.
                  </span>
                  <span className="text-gray-600">{q.correctAnswer || "?"}</span>
                  {resolveQuestionPoint(q, examType) !== undefined && (
                    <span className="text-xs text-red-600 font-semibold">
                      ({formatPoint(resolveQuestionPoint(q, examType))} điểm)
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* action buttons for whole group */}
          <div className="flex-shrink-0 flex flex-col gap-0.5 items-center">
            <button
              onClick={onMoveUp}
              disabled={isFirst}
              className="text-gray-300 hover:text-blue-500 p-1 rounded hover:bg-blue-50 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
              title="Di chuyển lên"
            >
              <ChevronUp size={14} />
            </button>
            <button
              onClick={onEdit}
              className="text-gray-300 hover:text-indigo-500 p-1 rounded hover:bg-indigo-50 transition-colors"
              title="Chỉnh sửa nhóm"
            >
              <Edit2 size={14} />
            </button>
            <button
              onClick={() => onDelete()}
              className="text-gray-300 hover:text-red-500 p-1 rounded hover:bg-red-50 transition-colors"
              title="Xóa nhóm"
            >
              <Trash2 size={14} />
            </button>
            <button
              onClick={onMoveDown}
              disabled={isLast}
              className="text-gray-300 hover:text-blue-500 p-1 rounded hover:bg-blue-50 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
              title="Di chuyển xuống"
            >
              <ChevronDown size={14} />
            </button>
          </div>
        </div>
      </div>

    </>
  );
};

// ─── Question Preview Item ────────────────────────────────────────────────────
const QuestionPreviewItem = ({ questionItem, index, onDelete, onEdit, onMoveUp, onMoveDown, isFirst, isLast, examType }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { question, type, contentQuestions, imageUrl } = questionItem;
  const point = resolveQuestionPoint(questionItem, examType);
  const selectedChoiceAnswer = type === "TN" ? normalizeChoiceAnswer(questionItem.correctAnswer) : "";
  const selectedDsAnswer = type === "DS" ? normalizeDsAnswer(questionItem.correctAnswer) : "";

  let displayNum = index + 1;
  if (question && question.match(/\d+/)) {
    displayNum = question.match(/\d+/)[0];
  }

  return (
    <>
      <div className="p-5 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-all">
        <div className="flex gap-4">
          {/* Number in circle */}
          <div className="flex-shrink-0">
            <div className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center font-bold text-sm border border-red-100">
              {displayNum}
            </div>
            {/* Type badge */}
            <div className="mt-2 text-center">
              <span className="text-[10px] font-bold text-gray-400 uppercase border border-gray-200 px-1 rounded bg-gray-50">
                {type}
              </span>
            </div>
            {point !== undefined && (
              <div className="mt-2 text-center text-[11px] font-semibold text-red-600">
                {formatPoint(point)} điểm
              </div>
            )}
          </div>

          <div className="flex-grow">
            {/* Image */}
            {imageUrl && (
              <div className="mb-4">
                <img
                  src={imageUrl}
                  alt="Question visual"
                  className="max-h-48 rounded-lg border border-gray-100"
                />
              </div>
            )}

            {/* Content */}
            {contentQuestions && (
              <div className="text-gray-800 mb-4 whitespace-pre-wrap leading-relaxed font-medium">
                {contentQuestions}
              </div>
            )}

            {/* Options TN */}
            {type === "TN" && (
              <div className="space-y-2">
                {["A", "B", "C", "D"].map((opt) => {
                  const key = `contentAnswer${opt}`;
                  const content = questionItem[key];
                  if (!content) return null;
                  const isSelected = selectedChoiceAnswer === opt;
                  return (
                    <div
                      key={opt}
                      className={`flex items-start gap-3 p-2 rounded border transition-all ${isSelected ? "bg-gray-50 border-gray-200" : "border-transparent"
                        }`}
                    >
                      <div className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center ${isSelected ? "border-red-400" : "border-gray-300"
                        }`}>
                        <div className={`w-2.5 h-2.5 rounded-full ${isSelected ? "bg-red-500" : "bg-transparent"
                          }`}></div>
                      </div>
                      <div className="flex gap-2">
                        <span className="font-bold text-gray-700 min-w-[1.5rem]">
                          {opt}.
                        </span>
                        <span className={isSelected ? "text-gray-700 font-medium" : "text-gray-600"}>{content}</span>
                      </div>
                    </div>
                  );
                })}
                {/* Dynamic options E-Z */}
                {[...Array(22)].map((_, i) => {
                  const char = String.fromCharCode(69 + i);
                  const key = `contentAnswer${char}`;
                  const content = questionItem[key];
                  if (!content) return null;
                  const isSelected = selectedChoiceAnswer === char;
                  return (
                    <div
                      key={char}
                      className={`flex items-start gap-3 p-2 rounded border transition-all ${isSelected ? "bg-gray-50 border-gray-200" : "border-transparent"
                        }`}
                    >
                      <div className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center ${isSelected ? "border-red-400" : "border-gray-300"
                        }`}>
                        <div className={`w-2.5 h-2.5 rounded-full ${isSelected ? "bg-red-500" : "bg-transparent"
                          }`}></div>
                      </div>
                      <div className="flex gap-2">
                        <span className="font-bold text-gray-700 min-w-[1.5rem]">
                          {char}.
                        </span>
                        <span className={isSelected ? "text-gray-700 font-medium" : "text-gray-600"}>{content}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Options DS (True/False) */}
            {type === "DS" && (
              <div className="space-y-2">
                {["A", "B"].map((opt) => {
                  const content =
                    questionItem[`contentAnswer${opt}`] ||
                    (opt === "A" ? "True" : "False");
                  const isSelected = selectedDsAnswer === opt;
                  return (
                    <div
                      key={opt}
                      className={`flex items-center gap-3 p-1 rounded border transition-all ${isSelected
                          ? "bg-gray-50 border-gray-200"
                          : "border-transparent text-gray-500"
                        }`}
                    >
                      <div
                        className={`w-5 h-5 rounded-full border flex items-center justify-center ${isSelected ? "border-red-400" : "border-gray-300"
                          }`}
                      >
                        <div
                          className={`w-2.5 h-2.5 rounded-full ${isSelected ? "bg-red-500" : "bg-transparent"
                            }`}
                        ></div>
                      </div>
                      <span className="font-bold text-gray-700 min-w-[1.5rem]">{opt}.</span>
                      <span className={`${isSelected ? "text-gray-700 font-medium" : "text-gray-600"}`}>
                        {content}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Type MT (Matching) – individual sub-question input */}
            {type === "MT" && (
              <div className="flex items-center gap-3 bg-gray-50 p-3 rounded border border-gray-100">
                <div className="text-xs text-gray-500 font-semibold">Đáp án:</div>
                <div className="border-2 border-red-300 rounded-lg px-4 py-2 font-bold text-red-700 bg-white text-lg min-w-[3rem] text-center">
                  {questionItem.correctAnswer || "?"}
                </div>
                {questionItem.matchGroup && (
                  <div className="text-xs text-gray-400 italic">
                    Nhóm: {questionItem.matchGroup} · #{questionItem.matchIndex + 1}/{questionItem.matchTotal}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action buttons: ↑ Edit ✕ ↓ – only render once per matching group */}
          {!(questionItem.matchGroup && questionItem.matchIndex > 0) && (
            <div className="flex-shrink-0 flex flex-col gap-0.5 items-center">
              <button
                onClick={onMoveUp}
                disabled={isFirst}
                className="text-gray-300 hover:text-blue-500 p-1 rounded hover:bg-blue-50 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                title="Di chuyển lên"
              >
                <ChevronUp size={14} />
              </button>
              <button
                onClick={onEdit}
                className="text-gray-300 hover:text-indigo-500 p-1 rounded hover:bg-indigo-50 transition-colors"
                title="Chỉnh sửa"
              >
                <Edit2 size={14} />
              </button>
              <button
                onClick={() => setConfirmOpen(true)}
                className="text-gray-300 hover:text-red-500 p-1 rounded hover:bg-red-50 transition-colors"
                title="Xóa câu hỏi"
              >
                <Trash2 size={14} />
              </button>
              <button
                onClick={onMoveDown}
                disabled={isLast}
                className="text-gray-300 hover:text-blue-500 p-1 rounded hover:bg-blue-50 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                title="Di chuyển xuống"
              >
                <ChevronDown size={14} />
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Xác nhận xóa câu hỏi"
        message="Bạn có chắc chắn muốn xóa câu hỏi này? Hành động này không thể hoàn tác."
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          onDelete();
        }}
        confirmText="Xóa"
      />
    </>
  );
};

export default function ExamForm({
  formExamData,
  classOptions,
  audioInputRef,
  handleChangeInputQuestion,
  addReadingQuestion,
  addListeningQuestion,
  addWritingQuestion,
  handleUpsertExam,
  handleCancel,
  questionsData,
  handleUploadAudio,
  handleDeleteAudio,
  handleDownloadSampleForSection,
  handleImportExcelForSection,
  handleDeleteQuestion,
  handleEditQuestion,
  handleReorderQuestion,
  handleMoveGroup,
}) {
  const [visibleSection, setVisibleSection] = useState("LISTENING");
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const isD4 = formExamData?.type === "D4";

  // When type switches to D4, force READING section
  useEffect(() => {
    if (formExamData?.type === "D4") {
      setVisibleSection("READING");
    }
  }, [formExamData?.type]);

  // When type changes to D4, auto-configure time and skillTimes defaults
  const handleTypeChange = (e) => {
    handleChangeInputQuestion(e);
    if (e.target.value === "D4") {
      setVisibleSection("READING");
      handleChangeInputQuestion({ target: { name: "time", value: 60 } });
      handleChangeInputQuestion({
        target: {
          name: "skillTimes",
          value: { listening: 0, reading: 60, writing: 0 },
        },
      });
    }
  };

  // For D4: when time changes, keep skillTimes.reading in sync with total time
  const handleD4TimeChange = (e) => {
    const newTime = Number(e.target.value);
    handleChangeInputQuestion({ target: { name: "time", value: newTime } });
    handleChangeInputQuestion({
      target: {
        name: "skillTimes",
        value: { listening: 0, reading: newTime, writing: 0 },
      },
    });
  };

  const handleAddManual = (fn) => {
    fn();
    toast.warn("Hãy nhấn hoàn thành nếu muốn lưu các câu đã nhập");
  };

  const handleAllowedClassChange = (event) => {
    const value = event.target.value;
    handleChangeInputQuestion({
      target: {
        name: "allowedClassIds",
        value: typeof value === "string" ? value.split(",") : value,
      },
    });
  };

  // when the parent clears audioUrl (e.g. removal or loading a draft without audio) make sure
  // the hidden file input is also reset so subsequent uploads still fire onChange.
  useEffect(() => {
    if (!formExamData.audioUrl && audioInputRef && audioInputRef.current) {
      audioInputRef.current.value = null;
    }
  }, [formExamData.audioUrl, audioInputRef]);
  // Filter questions by section with original index tracking
  const readingQuestions = questionsData
    .map((q, i) => ({ ...q, _origIndex: i }))
    .filter((q) => q.section === "READING" || !q.section);
  const listeningQuestions = questionsData
    .map((q, i) => ({ ...q, _origIndex: i }))
    .filter((q) => q.section === "LISTENING");
  const writingQuestions = questionsData
    .map((q, i) => ({ ...q, _origIndex: i }))
    .filter((q) => q.section === "WRITING");

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mt-10">
      {/* Main Form */}
      <div className="p-6">
        {/* Basic Information Section */}
        <div className="mb-8">
          <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-500" />
                {"Th\u00f4ng Tin C\u01a1 B\u1ea3n"}
              </h2>
              <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap">
                Tổng điểm: <span className="text-lg text-blue-600">{calculateTotalPoints(questionsData).toFixed(1)}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="contained"
                onClick={handleCancel}
                className="bg-red-600 hover:bg-red-700 px-6 normal-case shadow-none"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleUpsertExam}
                startIcon={<CheckCircle size={18} />}
                className="bg-green-600 hover:bg-green-700 px-6 normal-case shadow-none"
              >
                Save Exam
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <TextField
              label={"T\u00ean \u0111\u1ec1 thi *"}
              name="title"
              value={formExamData.title}
              onChange={handleChangeInputQuestion}
              variant="outlined"
              fullWidth
              size="small"
            />

            {isD4 ? (
              <TextField
                select
                label={"Th\u1eddi gian thi *"}
                name="time"
                value={formExamData?.time ?? 60}
                onChange={handleD4TimeChange}
                variant="outlined"
                fullWidth
                size="small"
              >
                <MenuItem value={60}>60 phút</MenuItem>
                <MenuItem value={0}>Không giới hạn thời gian</MenuItem>
              </TextField>
            ) : (
              <TextField
                type="number"
                label={"Thời gian thi (phút) *"}
                name="time"
                value={formExamData?.time}
                onChange={handleChangeInputQuestion}
                variant="outlined"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TextField
              select
              label={"K\u00ec Thi *"}
              name="type"
              value={formExamData?.type}
              onChange={handleTypeChange}
              variant="outlined"
              fullWidth
              size="small"
            >
              {["HSK1", "HSK2", "HSK3", "HSK4", "HSK5", "HSK6", "D4"].map(
                (option, key) => (
                  <MenuItem key={key} value={option}>
                    {option}
                  </MenuItem>
                )
              )}
            </TextField>

            <TextField
              select
              label={"Truy c\u1eadp *"}
              name="access"
              value={formExamData?.access}
              onChange={handleChangeInputQuestion}
              variant="outlined"
              fullWidth
              size="small"
            >
              {["PUBLIC", "PRIVATE"].map((option, key) => (
                <MenuItem key={key} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <TextField
              type="datetime-local"
              label="Thời gian mở bài thi"
              name="startTime"
              value={formExamData?.startTime || ""}
              onChange={handleChangeInputQuestion}
              variant="outlined"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              helperText="Để trống nếu không giới hạn giờ mở"
            />

            <TextField
              type="datetime-local"
              label="Thời gian đóng bài thi"
              name="endTime"
              value={formExamData?.endTime || ""}
              onChange={handleChangeInputQuestion}
              variant="outlined"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              helperText="Để trống nếu không giới hạn giờ đóng"
            />
          </div>

          {formExamData?.access === "PRIVATE" ? (
            <div className="mt-4">
              <TextField
                select
                label={"Lớp được phép làm bài *"}
                name="allowedClassIds"
                value={formExamData?.allowedClassIds || []}
                onChange={handleAllowedClassChange}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => {
                    const values = Array.isArray(selected) ? selected : [];
                    const labels = values
                      .map((classId) => classOptions?.find((item) => item?._id === classId)?.name || classId)
                      .filter(Boolean);
                    return labels.join(", ");
                  },
                }}
                variant="outlined"
                fullWidth
                size="small"
                helperText="Chọn một hoặc nhiều lớp được phép làm đề private"
              >
                {(classOptions || []).map((classItem) => (
                  <MenuItem key={classItem?._id} value={classItem?._id}>
                    {classItem?.name} ({classItem?.classCode})
                  </MenuItem>
                ))}
              </TextField>
            </div>
          ) : null}
        </div>

        {/* Skill Times Section */}
        <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            {"Phân Bổ Thời Gian Các Kỹ Năng"}
          </h2>

          {isD4 ? (
            <>
              <p className="text-sm text-gray-600 mb-4">
                {"Đề D4 chỉ có section Reading. Thời gian Reading tự động bằng tổng thời gian thi: "}
                <span className="font-bold text-gray-900">
                  {formExamData?.time === 0 ? "Không giới hạn" : `${formExamData?.time || 60} phút`}
                </span>
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-4">
                <TextField
                  label={"Đọc (Reading) - phút"}
                  value={formExamData?.time === 0 ? "Không giới hạn" : String(formExamData?.skillTimes?.reading ?? formExamData?.time ?? 60)}
                  disabled
                  variant="outlined"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  helperText={"Tự động đồng bộ với tổng thời gian thi"}
                />
              </div>
              <div className="flex items-center gap-2 text-green-700 bg-green-50 p-3 rounded border border-green-300">
                <CheckCircle size={18} className="text-green-600" />
                <span>{"Đề D4: Thời gian Reading được tự động đồng bộ với tổng thời gian thi."}</span>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">
                {"Tổng thời gian các kỹ năng (Listening + Reading + Writing) phải bằng tổng thời gian thi: "}<span className="font-bold text-gray-900">{formExamData?.time || 0}{" phút"}</span>
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-4">
                <TextField
                  type="number"
                  label={"Nghe (Listening) - phút *"}
                  name="listening"
                  value={formExamData?.skillTimes?.listening ?? ""}
                  onChange={(e) => {
                    const { name, value } = e.target;
                    const normalized = value === "" ? "" : Number(value);
                    handleChangeInputQuestion({
                      target: {
                        name: "skillTimes",
                        value: {
                          ...formExamData.skillTimes,
                          [name]: normalized,
                        },
                      },
                    });
                  }}
                  variant="outlined"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0 }}
                />

                <TextField
                  type="number"
                  label={"Đọc (Reading) - phút *"}
                  name="reading"
                  value={formExamData?.skillTimes?.reading ?? ""}
                  onChange={(e) => {
                    const { name, value } = e.target;
                    const normalized = value === "" ? "" : Number(value);
                    handleChangeInputQuestion({
                      target: {
                        name: "skillTimes",
                        value: {
                          ...formExamData.skillTimes,
                          [name]: normalized,
                        },
                      },
                    });
                  }}
                  variant="outlined"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0 }}
                />

                <TextField
                  type="number"
                  label={"Viết (Writing) - phút *"}
                  name="writing"
                  value={formExamData?.skillTimes?.writing ?? ""}
                  onChange={(e) => {
                    const { name, value } = e.target;
                    const normalized = value === "" ? "" : Number(value);
                    handleChangeInputQuestion({
                      target: {
                        name: "skillTimes",
                        value: {
                          ...formExamData.skillTimes,
                          [name]: normalized,
                        },
                      },
                    });
                  }}
                  variant="outlined"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0 }}
                />
              </div>

              {/* Validation Message */}
              {(() => {
                const skillTimes = formExamData?.skillTimes || {};
                const listeningTime = parseInt(skillTimes.listening || 0);
                const readingTime = parseInt(skillTimes.reading || 0);
                const writingTime = parseInt(skillTimes.writing || 0);
                const totalSkillTime = listeningTime + readingTime + writingTime;
                const totalTime = parseInt(formExamData?.time || 0);

                if (totalSkillTime === totalTime) {
                  return (
                    <div className="flex items-center gap-2 text-green-700 bg-green-50 p-3 rounded border border-green-300">
                      <CheckCircle size={18} className="text-green-600" />
                      <span>{"Phân bổ thời gian hợp lệ!"}</span>
                    </div>
                  );
                } else {
                  return (
                    <div className="flex items-center gap-2 text-red-700 bg-red-50 p-3 rounded border border-red-300">
                      <XCircle size={18} className="text-red-600" />
                      <span>
                        {`Tổng thời gian các kỹ năng (${totalSkillTime} phút) phải bằng ${totalTime} phút`}
                      </span>
                    </div>
                  );
                }
              })()}
            </>
          )}
        </div>

        {/* Questions Section */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Layers className="w-5 h-5 text-gray-500" />
            {"Qu\u1ea3n L\u00fd C\u00e2u H\u1ecfi"}
          </h2>
          {/* section tabs */}
          <div className="flex gap-2 mb-4">
            {[
              { label: "Listening", key: "LISTENING" },
              { label: "Reading", key: "READING" },
              { label: "Writing", key: "WRITING" },
            ]
              .filter((s) => !isD4 || s.key === "READING")
              .map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className={`px-3 py-1 rounded-md text-sm font-medium focus:outline-none ${visibleSection === s.key
                      ? "bg-red-600 text-white"
                      : "bg-gray-200 text-gray-700"
                    }`}
                  onClick={() => setVisibleSection(s.key)}
                >
                  {s.label}
                </button>
              ))}
          </div>

          {/* ── Section Listening ───────────────────────────────────── */}
          {visibleSection === "LISTENING" && (
            <>
              <div className="mb-4 flex flex-row justify-between items-center mt-6">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-700">
                    Section Listening - {"Danh s\u00e1ch c\u00e2u h\u1ecfi nghe"}
                  </span>
                  <div className="bg-orange-100 text-orange-800 px-3 py-1 rounded text-xs font-semibold">
                    Tổng: {calculateTotalPoints(listeningQuestions).toFixed(1)} điểm
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <SectionExcelButtons
                    section="LISTENING"
                    handleDownloadSampleForSection={handleDownloadSampleForSection}
                    handleImportExcelForSection={handleImportExcelForSection}
                  />
                  <Button
                    component="label"
                    variant="outlined"
                    className="h-8 text-xs border-red-500 text-red-600 hover:bg-red-50 normal-case"
                    startIcon={<UploadCloud size={14} />}
                  >
                    Upload Audio
                    <VisuallyHiddenInput
                      type="file"
                      accept="audio/*"
                      onChange={handleUploadAudio}
                      ref={audioInputRef}
                    />
                  </Button>
                  <Button
                    variant="outlined"
                    className="h-8 text-xs border-red-500 text-red-600 hover:bg-red-50 normal-case"
                    startIcon={<Plus size={14} />}
                    onClick={() => handleAddManual(addListeningQuestion)}
                  >
                    {"Th\u00eam c\u00e2u th\u1ee7 c\u00f4ng"}
                  </Button>
                </div>
              </div>

              {formExamData.audioUrl && (
                <div className="w-full bg-gray-50 p-3 rounded border border-gray-200 mb-3">
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-medium text-gray-700">
                      Audio File:
                    </div>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Trash2 size={16} />}
                      onClick={handleDeleteAudio}
                      className="normal-case hover:bg-red-50"
                    >
                      Remove
                    </Button>
                  </div>
                  <audio controls className="w-full" key={formExamData.audioUrl}>
                    <source src={formExamData.audioUrl} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}

              {visibleSection === "LISTENING" && (listeningQuestions.length === 0 ? (
                <p className="text-gray-500 italic mb-5">
                  {"Ch\u01b0a c\u00f3 c\u00e2u h\u1ecfi n\u00e0o \u0111\u01b0\u1ee3c th\u00eam."}
                </p>
              ) : (
                <div className="space-y-4 mb-5">
                  {buildDisplayItems(listeningQuestions).map((item, dispIdx, arr) => {
                    const isFirst = dispIdx === 0;
                    const isLast = dispIdx === arr.length - 1;
                    if (item.type === "single") {
                      return (
                        <QuestionPreviewItem
                          key={item.index}
                          questionItem={item.question}
                          index={item.index}
                          examType={formExamData?.type}
                          onDelete={() => handleDeleteQuestion(item.index)}
                          onEdit={() => handleEditQuestion(item.index)}
                          onMoveUp={() => handleReorderQuestion(item.index, item.index - 1)}
                          onMoveDown={() => handleReorderQuestion(item.index, item.index + 1)}
                          isFirst={isFirst}
                          isLast={isLast}
                        />
                      );
                    }
                    // group
                    return (
                      <GroupPreviewItem
                        key={item.startIndex}
                        group={item.questions}
                        startIndex={item.startIndex}
                        examType={formExamData?.type}
                        onDelete={() => handleDeleteQuestion(item.startIndex, item.questions.length)}
                        onEdit={() => handleEditQuestion(item.startIndex)}
                        onMoveUp={() => handleMoveGroup(item.startIndex, item.questions.length, -1)}
                        onMoveDown={() => handleMoveGroup(item.startIndex, item.questions.length, 1)}
                        isFirst={isFirst}
                        isLast={isLast}
                      />
                    );
                  })}
                </div>
              ))}

            </>
          )}

          {/* ── Section Reading ─────────────────────────────────────── */}
          {visibleSection === "READING" && (
            <>
              <div className="mb-4 flex flex-row justify-between items-center mt-6">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-700">
                    Section Reading - {"Danh s\u00e1ch c\u00e2u h\u1ecfi \u0111\u1ecdc"}
                  </span>
                  <div className="bg-green-100 text-green-800 px-3 py-1 rounded text-xs font-semibold">
                    Tổng: {calculateTotalPoints(readingQuestions).toFixed(1)} điểm
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <SectionExcelButtons
                    section="READING"
                    handleDownloadSampleForSection={handleDownloadSampleForSection}
                    handleImportExcelForSection={handleImportExcelForSection}
                  />
                  <Button
                    variant="outlined"
                    className="h-8 text-xs border-red-500 text-red-600 hover:bg-red-50 normal-case"
                    startIcon={<Plus size={14} />}
                    onClick={() => addReadingQuestion()}
                  >
                    {"Th\u00eam c\u00e2u th\u1ee7 c\u00f4ng"}
                  </Button>
                </div>
              </div>

              {readingQuestions.length === 0 ? (
                <p className="text-gray-500 italic mb-5">
                  {"Ch\u01b0a c\u00f3 c\u00e2u h\u1ecfi n\u00e0o \u0111\u01b0\u1ee3c th\u00eam."}
                </p>
              ) : (
                <div className="space-y-4 mb-5">
                  {buildDisplayItems(readingQuestions).map((item, dispIdx, arr) => {
                    const isFirst = dispIdx === 0;
                    const isLast = dispIdx === arr.length - 1;
                    if (item.type === "single") {
                      return (
                        <QuestionPreviewItem
                          key={item.index}
                          questionItem={item.question}
                          index={item.index}
                          examType={formExamData?.type}
                          onDelete={() => handleDeleteQuestion(item.index)}
                          onEdit={() => handleEditQuestion(item.index)}
                          onMoveUp={() => handleReorderQuestion(item.index, item.index - 1)}
                          onMoveDown={() => handleReorderQuestion(item.index, item.index + 1)}
                          isFirst={isFirst}
                          isLast={isLast}
                        />
                      );
                    }
                    return (
                      <GroupPreviewItem
                        key={item.startIndex}
                        group={item.questions}
                        startIndex={item.startIndex}
                        examType={formExamData?.type}
                        onDelete={() => handleDeleteQuestion(item.startIndex, item.questions.length)}
                        onEdit={() => handleEditQuestion(item.startIndex)}
                        onMoveUp={() => handleMoveGroup(item.startIndex, item.questions.length, -1)}
                        onMoveDown={() => handleMoveGroup(item.startIndex, item.questions.length, 1)}
                        isFirst={isFirst}
                        isLast={isLast}
                      />
                    );
                  })}
                </div>
              )}

            </>
          )}

          {/* ── Section Writing ─────────────────────────────────────── */}
          {visibleSection === "WRITING" && (
            <>
              <div className="mb-4 flex flex-row justify-between items-center mt-6">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-700">
                    Section Writing - {"Danh s\u00e1ch c\u00e2u h\u1ecfi vi\u1ebft"}
                  </span>
                  <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded text-xs font-semibold">
                    Tổng: {calculateTotalPoints(writingQuestions).toFixed(1)} điểm
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <SectionExcelButtons
                    section="WRITING"
                    handleDownloadSampleForSection={handleDownloadSampleForSection}
                    handleImportExcelForSection={handleImportExcelForSection}
                  />
                  <Button
                    variant="outlined"
                    className="h-8 text-xs border-red-500 text-red-600 hover:bg-red-50 normal-case"
                    startIcon={<Plus size={14} />}
                    onClick={() => addWritingQuestion()}
                  >
                    {"Th\u00eam c\u00e2u th\u1ee7 c\u00f4ng"}
                  </Button>
                </div>
              </div>

              {writingQuestions.length === 0 ? (
                <p className="text-gray-500 italic mb-5">
                  {"Ch\u01b0a c\u00f3 c\u00e2u h\u1ecfi n\u00e0o \u0111\u01b0\u1ee3c th\u00eam."}
                </p>
              ) : (
                <div className="space-y-4 mb-5">
                  {buildDisplayItems(writingQuestions).map((item, dispIdx, arr) => {
                    const isFirst = dispIdx === 0;
                    const isLast = dispIdx === arr.length - 1;
                    if (item.type === "single") {
                      return (
                        <QuestionPreviewItem
                          key={item.index}
                          questionItem={item.question}
                          index={item.index}
                          examType={formExamData?.type}
                          onDelete={() => handleDeleteQuestion(item.index)}
                          onEdit={() => handleEditQuestion(item.index)}
                          onMoveUp={() => handleReorderQuestion(item.index, item.index - 1)}
                          onMoveDown={() => handleReorderQuestion(item.index, item.index + 1)}
                          isFirst={isFirst}
                          isLast={isLast}
                        />
                      );
                    }
                    return (
                      <GroupPreviewItem
                        key={item.startIndex}
                        group={item.questions}
                        startIndex={item.startIndex}
                        examType={formExamData?.type}
                        onDelete={() => handleDeleteQuestion(item.startIndex, item.questions.length)}
                        onEdit={() => handleEditQuestion(item.startIndex)}
                        onMoveUp={() => handleMoveGroup(item.startIndex, item.questions.length, -1)}
                        onMoveDown={() => handleMoveGroup(item.startIndex, item.questions.length, 1)}
                        isFirst={isFirst}
                        isLast={isLast}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}

          <Divider className="my-2 border-gray-100" />

          {/* Final Actions */}
          <div className="flex gap-4 justify-end">
            <Button
              variant="contained"
              className="bg-red-600 hover:bg-red-700 px-6 normal-case shadow-none"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpsertExam}
              startIcon={<CheckCircle size={18} />}
              className="bg-green-600 hover:bg-green-700 px-6 normal-case shadow-none"
            >
              Save Exam
            </Button>
            <ConfirmDialog
              open={cancelConfirmOpen}
              title="Xác nhận hủy"
              message="Bạn có chắc muốn hủy? Mọi thay đổi sẽ không được ghi nhận!"
              onCancel={() => setCancelConfirmOpen(false)}
              onConfirm={() => {
                setCancelConfirmOpen(false);
                if (handleCancel) handleCancel();
              }}
              confirmText="Hủy"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
